import { useMemo } from 'react';
import { Badge, Button, Group, SegmentedControl, Select, Stack, Text } from '@mantine/core';
import {
	EMPTY_FLOW_RULE_SCENARIO,
	flowRuleInputs,
} from '@dealstack/shared/lib/common/business/documentFlow';
import type {
	FlowRuleInput,
	FlowRuleOutcome,
	FlowRules,
	FlowRuleScenario,
} from '@dealstack/shared/lib/common/business/documentFlow';
import type { DataPointWithSampleRes } from '@dealstack/frontend/shared/components/FieldBinding/BindPanel/BindPanel.types.ts';
import { choiceSourceForPath } from '@dealstack/shared/lib/documents/dataPointChoices';

import {
	answerControlValue,
	answerFromControl,
	choiceOptions,
	cohortControlValue,
	cohortFromControl,
	isEmptyScenario,
	outcomeBadge,
	RECORDED,
	SEGMENT_OPTIONS,
	valuesNamedByRules,
	withScenarioAnswer,
	withScenarioCohort,
} from './rulePreview.utils';
import type { RuleCohort } from './rules.types';

interface Props {
	rules: FlowRules;
	dataPoints: DataPointWithSampleRes[];
	cohorts: RuleCohort[];
	scenario: FlowRuleScenario;
	onChange: (scenario: FlowRuleScenario) => void;
	/** From the render on screen; undefined while the next one is in flight. */
	outcomes: FlowRuleOutcome[] | undefined;
	/** No recipient, so an unset answer leaves its rules undecided rather than "as recorded". */
	isFieldsView: boolean;
}

/**
 * Answers for everything the document's rules read, set by hand, so the author can watch the
 * preview change the way a recipient's data would change it.
 *
 * One control per DATA POINT, not per rule, so two rules reading the same country cannot be set
 * to disagree. "Not answered" and "Not recorded" show what a recipient with no answer gets, which
 * is nothing. Nothing here is saved: it is how the author is looking at the document.
 */
export const RulePreviewPanel = ({
	rules,
	dataPoints,
	cohorts,
	scenario,
	onChange,
	outcomes,
	isFieldsView,
}: Props) => {
	const inputs = useMemo(() => flowRuleInputs(rules), [rules]);
	const outcomeByRule = useMemo(
		() => new Map((outcomes ?? []).map((outcome) => [outcome.ruleId, outcome])),
		[outcomes]
	);
	const recordedLabel = isFieldsView ? 'Not set' : 'As recorded';

	const titleFor = (input: FlowRuleInput): string => {
		if (input.kind === 'cohort') {
			const name =
				cohorts.find((cohort) => cohort.id === input.cohortId)?.name ??
				input.cohortName ??
				'A cohort';
			return `Cohort · ${name}`;
		}
		const point = dataPoints.find((candidate) => candidate.path === input.dataPointPath);
		return point === undefined ? input.dataPointPath : `${point.group} · ${point.label}`;
	};

	const controlFor = (input: FlowRuleInput) => {
		if (input.kind === 'cohort') {
			return (
				<SegmentedControl
					size="xs"
					fullWidth
					value={cohortControlValue(scenario.cohorts[input.cohortId])}
					onChange={(value) =>
						onChange(
							withScenarioCohort(scenario, input.cohortId, cohortFromControl(value))
						)
					}
					data={[{ value: RECORDED, label: recordedLabel }, ...SEGMENT_OPTIONS.cohort]}
				/>
			);
		}
		const answer = scenario.dataPoints[input.dataPointPath];
		const setAnswer = (value: string | null): void =>
			onChange(
				withScenarioAnswer(
					scenario,
					input.dataPointPath,
					answerFromControl(input.kind, value)
				)
			);
		if (input.kind === 'choice') {
			// Off the shared catalogue, which is the same thing `flowRuleInputs` asks before it
			// settles on a `choice` control — read off the API's copy instead, a point the
			// response happens not to carry rendered no control at all.
			const source = choiceSourceForPath(input.dataPointPath);
			if (source === undefined) {
				return null;
			}
			return (
				<Select
					size="xs"
					searchable
					allowDeselect={false}
					value={answerControlValue(input.kind, answer)}
					onChange={setAnswer}
					data={choiceOptions(source, valuesNamedByRules(input, rules), recordedLabel)}
				/>
			);
		}
		return (
			<SegmentedControl
				size="xs"
				fullWidth
				value={answerControlValue(input.kind, answer)}
				onChange={setAnswer}
				data={[{ value: RECORDED, label: recordedLabel }, ...SEGMENT_OPTIONS[input.kind]]}
			/>
		);
	};

	return (
		// Arrow keys belong to these controls, not to the preview's step-through-recipients
		// shortcut — see usePreviewInvestorShortcuts.
		<Stack gap="sm" data-testid="rule-preview-panel" data-preview-shortcuts="off">
			<Group justify="space-between" wrap="nowrap">
				<Text size="sm" fw={600}>
					Try the rules
				</Text>
				{!isEmptyScenario(scenario) && (
					<Button
						size="compact-xs"
						variant="subtle"
						onClick={() => onChange(EMPTY_FLOW_RULE_SCENARIO)}
						data-testid="rule-preview-reset"
					>
						{isFieldsView ? 'Clear answers' : 'Back to recorded answers'}
					</Button>
				)}
			</Group>

			<Text size="xs" c="dark.0">
				{isFieldsView
					? 'Set an answer to decide the rules that read it. Rules left unset show their content, labelled. Nothing here is saved.'
					: 'Change an answer to see this recipient’s document as it would be with it. The page prints your answer too. Nothing here is saved.'}
			</Text>

			{inputs.map((input) => {
				const key = input.kind === 'cohort' ? input.cohortId : input.dataPointPath;
				return (
					<Stack
						key={`${input.kind}-${key}`}
						gap={6}
						p="sm"
						style={{ border: '1px solid #dee2e6', borderRadius: 8 }}
						data-testid={`rule-preview-input-${key}`}
					>
						<Text size="xs" fw={600}>
							{titleFor(input)}
						</Text>
						{controlFor(input)}
						{input.ruleIds.map((ruleId) => {
							const outcome = outcomeByRule.get(ruleId);
							const badge = outcome === undefined ? undefined : outcomeBadge(outcome);
							return (
								<Group key={ruleId} justify="space-between" wrap="nowrap" gap="xs">
									<Text size="xs" truncate>
										{rules[ruleId]?.label ?? ruleId}
									</Text>
									{badge !== undefined && (
										<Badge
											size="xs"
											variant="light"
											color={badge.color}
											data-testid={`rule-preview-outcome-${ruleId}`}
										>
											{badge.label}
										</Badge>
									)}
								</Group>
							);
						})}
					</Stack>
				);
			})}
		</Stack>
	);
};
